import {
    CONVERTIBLE_IMAGE_EXTENSIONS,
    GIF_IMAGE_EXTENSIONS,
} from '../modules/file/config/image-upload';

export const CONSTANTS = {
    ALLOWED_FILES_ENQUIRY_INBOX: [
        ...GIF_IMAGE_EXTENSIONS,
        ...CONVERTIBLE_IMAGE_EXTENSIONS,
        'pdf',
        'doc',
        'docx',
        'txt',
        'xls',
        'xlsx',
    ],
    MAX_FILE_SIZE: 20 * 1024 * 1024, // 20MB in bytes
    MAX_TOTAL_FILES: 10,
    FILE_ERROR_MESSAGES: {
        TOO_LARGE: 'File is too large. Maximum size is 20MB.',
        INVALID_TYPE:
            'Invalid file type. Please check the list of allowed file types.',
        TOO_MANY_FILES: 'Too many files. Maximum number of files is 10.',
        DUPLICATE_FILE: 'This file has already been added.',
    },
} as const;

// First, let's create a MIME type mapping for the allowed file extensions
export const MIME_TYPE_MAPPING: { [key: string]: string[] } = {
    // Images
    jpg: ['image/jpeg'],
    jpeg: ['image/jpeg'],
    png: ['image/png'],
    gif: ['image/gif'],

    // Documents
    pdf: ['application/pdf'],
    doc: ['application/msword'],
    docx: [
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ],
    xls: ['application/vnd.ms-excel'],
    xlsx: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],

    // Text
    txt: ['text/plain'],
};

/**
 * Truncates a filename if it exceeds a certain length.
 * Preserves the file extension and adds ellipsis in the middle.
 *
 * @example
 * truncateFilename('very-long-filename.pdf') // Returns 'very-long...name.pdf'
 * truncateFilename('short.pdf') // Returns 'short.pdf'
 *
 * @param filename - The filename to truncate
 * @param maxLength - Maximum length of the filename (default: 32)
 * @returns The truncated filename
 */
export const truncateFilename = (
    filename: string,
    maxLength: number = 32,
): string => {
    if (filename.length <= maxLength) {
        return filename;
    }

    const lastDotIndex = filename.lastIndexOf('.');
    const hasExtension = lastDotIndex > 0;

    if (!hasExtension) {
        const truncatedLength = maxLength - 3; // Account for '...'
        const start = Math.ceil(truncatedLength / 2);
        const end = Math.floor(truncatedLength / 2);
        return `${filename.slice(0, start)}...${filename.slice(-end)}`;
    }

    const extension = filename.slice(lastDotIndex + 1);
    const nameWithoutExtension = filename.slice(0, lastDotIndex);

    const truncatedLength = maxLength - extension.length - 4; // Account for '...' and '.'
    const start = Math.ceil(truncatedLength / 2);
    const end = Math.floor(truncatedLength / 2);

    return `${nameWithoutExtension.slice(0, start)}...${nameWithoutExtension.slice(-end)}.${extension}`;
};
